/**
 * DEMO_MODE figures for the MMM panel.
 *
 * The file's own banner already fenced this block off; it is a separate module
 * now for the same reason the sibling refactor separated its mocks — so that
 * fabricated numbers and measured ones cannot be confused for each other.
 */

import demoDataService, { scaleChannelsByPeriod } from './demoDataService';
import { getStaticDemoData } from './mediaIntelligenceShared';
import type { MMMStatus, MMMRecommendationsData } from './mediaIntelligenceTypes';

// ---------------------------------------------------------------------------
// DEMO_MODE figures
// ---------------------------------------------------------------------------
export const getMockMMMStatus = (): MMMStatus => ({
  progress: 100,
  kpiType: 'revenue',
  dataDays: 87,
  spanDays: 90,
  dataQuality: [],
  rSquared: 0.87,
  mape: 12.3,
  nextSteps: [
    'Add TikTok tracking to improve model accuracy',
    '15 more days of data needed for full model',
    'Include offline media spend for better predictions',
  ],
});

// Strategic reallocation strategy used by both option1 and option2.
// Re-targeting weights are kept static so the *narrative* (TikTok → Google) stays
// stable while the absolute budget numbers float with whatever the Campaign
// Performance aggregate is currently showing.
type MMMChannelKey =
  | 'facebook'
  | 'google_display'
  | 'google_search'
  | 'performance_max'
  | 'tiktok'
  | 'line';

type MMMChannelStrategy = {
  channelKey: MMMChannelKey;
  displayName: string;
  icon: string;
  // Per-stage split of THIS channel's budget. Must sum to 1.0.
  stageMix: { aware: number; consider: number; convert: number };
  // Per-stage ROI used for contribution display.
  stageRoi: { aware: number; consider: number; convert: number };
  // Multiplier vs current spend for each scenario.
  option1Multiplier: number; // Max Revenue
  option2Multiplier: number; // Max ROI
};

const MMM_CHANNEL_STRATEGY: MMMChannelStrategy[] = [
  { channelKey: 'facebook',        displayName: 'Facebook',        icon: '📘', stageMix: { aware: 0.40, consider: 0.30, convert: 0.30 }, stageRoi: { aware: 4.2, consider: 4.5, convert: 4.2 }, option1Multiplier: 1.25, option2Multiplier: 1.13 },
  { channelKey: 'google_display',  displayName: 'Google Display',  icon: '🖼️', stageMix: { aware: 0.70, consider: 0.20, convert: 0.10 }, stageRoi: { aware: 2.2, consider: 3.0, convert: 3.5 }, option1Multiplier: 0.95, option2Multiplier: 0.80 },
  { channelKey: 'google_search',   displayName: 'Google Search',   icon: '🔍', stageMix: { aware: 0.15, consider: 0.50, convert: 0.35 }, stageRoi: { aware: 2.8, consider: 5.2, convert: 7.5 }, option1Multiplier: 1.12, option2Multiplier: 1.09 },
  { channelKey: 'performance_max', displayName: 'Performance Max', icon: '⚡', stageMix: { aware: 0.30, consider: 0.30, convert: 0.40 }, stageRoi: { aware: 3.0, consider: 4.5, convert: 6.5 }, option1Multiplier: 1.10, option2Multiplier: 1.05 },
  { channelKey: 'tiktok',          displayName: 'TikTok',          icon: '🎵', stageMix: { aware: 0.50, consider: 0.30, convert: 0.20 }, stageRoi: { aware: 2.5, consider: 3.8, convert: 5.2 }, option1Multiplier: 0.78, option2Multiplier: 0.62 },
  { channelKey: 'line',            displayName: 'LINE Ads',        icon: '💚', stageMix: { aware: 0.15, consider: 0.35, convert: 0.50 }, stageRoi: { aware: 3.5, consider: 5.8, convert: 8.2 }, option1Multiplier: 1.50, option2Multiplier: 1.40 },
];

const buildStageBreakdown = (
  currentBudget: number,
  recommendedBudget: number,
  strat: MMMChannelStrategy
) => ({
  aware:    { current: Math.round(currentBudget * strat.stageMix.aware),     recommended: Math.round(recommendedBudget * strat.stageMix.aware),     roi: strat.stageRoi.aware,    contribution: Math.round(recommendedBudget * strat.stageMix.aware * strat.stageRoi.aware), volume: Math.round(recommendedBudget * strat.stageMix.aware * strat.stageRoi.aware), volumeLabel: 'Reach', volumes: [{ label: 'Reach', value: Math.round(recommendedBudget * strat.stageMix.aware * strat.stageRoi.aware) }] },
  consider: { current: Math.round(currentBudget * strat.stageMix.consider),  recommended: Math.round(recommendedBudget * strat.stageMix.consider),  roi: strat.stageRoi.consider, contribution: Math.round(recommendedBudget * strat.stageMix.consider * strat.stageRoi.consider), volume: Math.round(recommendedBudget * strat.stageMix.consider * strat.stageRoi.consider), volumeLabel: 'Engagements', volumes: [{ label: 'Engagements', value: Math.round(recommendedBudget * strat.stageMix.consider * strat.stageRoi.consider) }] },
  convert:  { current: Math.round(currentBudget * strat.stageMix.convert),   recommended: Math.round(recommendedBudget * strat.stageMix.convert),   roi: strat.stageRoi.convert,  contribution: Math.round(recommendedBudget * strat.stageMix.convert * strat.stageRoi.convert), volume: Math.round(recommendedBudget * strat.stageMix.convert * strat.stageRoi.convert), volumeLabel: 'Conversions', volumes: [{ label: 'Conversions', value: Math.round(recommendedBudget * strat.stageMix.convert * strat.stageRoi.convert) }] },
});

/**
 * MMM mock that derives baselines from the SAME data source the Campaign
 * Performance surface uses. /campaigns prefers the static JSON snapshot at
 * `public/demo-data/lead_gen.json` (via `getStaticDemoData`) and only falls
 * back to the dynamic generator when the snapshot is missing — mirror that
 * order here so the two surfaces always show identical totals.
 *
 * Strategic recommendation logic (which channel to grow / shrink) stays fixed
 * so the narrative is stable across reloads even when the data swaps.
 */
export const getMockMMMRecommendations = async (periodType = 'month'): Promise<MMMRecommendationsData> => {
  const staticData = await getStaticDemoData();
  const rawChannels = staticData?.channelPerformance ?? demoDataService.getDemoChannelPerformance();
  const channels = scaleChannelsByPeriod(rawChannels, periodType);
  const channelByKey = new Map(channels.map((c) => [c.key as MMMChannelStrategy['channelKey'], c]));

  const baselineSpend = channels.reduce((sum, c) => sum + (c.cost || 0), 0);
  const baselineRevenue = channels.reduce((sum, c) => sum + (c.revenue || 0), 0);
  const baselineRoi = baselineSpend > 0 ? baselineRevenue / baselineSpend : 0;

  // Build per-scenario channel rows from the live aggregate.
  const buildRows = (scenario: 'option1' | 'option2') => {
    return MMM_CHANNEL_STRATEGY.map((strat) => {
      const channel = channelByKey.get(strat.channelKey);
      const currentBudget = Math.round(channel?.cost ?? 0);
      const multiplier = scenario === 'option1' ? strat.option1Multiplier : strat.option2Multiplier;
      const recommendedBudget = Math.round(currentBudget * multiplier);
      const currentPercent = baselineSpend > 0 ? Math.round((currentBudget / baselineSpend) * 100) : 0;
      return {
        name: strat.displayName,
        currentBudget,
        recommendedBudget,
        currentPercent,
        recommendedPercent: 0, // filled in after we have the new total
        stageBreakdown: buildStageBreakdown(currentBudget, recommendedBudget, strat),
      };
    });
  };

  const finalizePercents = (rows: ReturnType<typeof buildRows>) => {
    const total = rows.reduce((s, r) => s + r.recommendedBudget, 0);
    return rows.map((r) => ({
      ...r,
      recommendedPercent: total > 0 ? Math.round((r.recommendedBudget / total) * 100) : 0,
    }));
  };

  const option1Rows = finalizePercents(buildRows('option1'));
  const option2Rows = finalizePercents(buildRows('option2'));

  const option1Spend = option1Rows.reduce((s, r) => s + r.recommendedBudget, 0);
  const option2Spend = option2Rows.reduce((s, r) => s + r.recommendedBudget, 0);
  const option1Revenue = option1Rows.reduce((s, r) => s + r.stageBreakdown.aware.contribution + r.stageBreakdown.consider.contribution + r.stageBreakdown.convert.contribution, 0);
  const option2Revenue = option2Rows.reduce((s, r) => s + r.stageBreakdown.aware.contribution + r.stageBreakdown.consider.contribution + r.stageBreakdown.convert.contribution, 0);

  // Display helpers
  const formatBaht = (n: number) =>
    n >= 1_000_000 ? `฿${(n / 1_000_000).toFixed(1)}M` : `฿${Math.round(n / 1000)}K`;
  const conversionsTotal = channels.reduce((s, c) => s + (c.conversions || 0), 0);

  return {
    // Demo figures are the heuristic's shape, and the caveat that renders from
    // this is the one that describes them accurately.
    engine: 'heuristic',
    computedAt: new Date().toISOString(),
    // Demo data carries revenue, so the revenue cells stay populated in demo mode.
    revenueAvailable: true,
    // The demo workspace's channels carry a real multiplier per channel, so
    // this fallback genuinely has a move to show. It is the one path here that
    // is allowed to say so without measuring anything.
    reallocationAvailable: true,
    // Every demo channel reports revenue, so roi's denominator IS the total.
    revenueCoveragePct: 100,
    revenueSpend: baselineSpend,
    confidence: 82,
    baseline: {
      label: 'Current',
      revenue: baselineRevenue,
      spend: baselineSpend,
      roi: Number(baselineRoi.toFixed(2)),
      projectedRevenue: baselineRevenue,
    },
    option1: {
      label: 'Maximize Result',
      badge: 'Recommended',
      action: 'Shift budget: TikTok → Google Search + LINE Ads',
      impact: `+${formatBaht(option1Revenue - baselineRevenue)} additional revenue projected`,
      explanation: 'Google Search shows highest ROAS at 4.2x with strong conversion performance.',
      roi: option1Spend > 0 ? Number((option1Revenue / option1Spend).toFixed(2)) : 0,
      projectedRevenue: option1Revenue,
      spend: option1Spend,
      change: baselineSpend > 0 ? `${((option1Spend - baselineSpend) / baselineSpend * 100 >= 0 ? '+' : '')}${Math.round((option1Spend - baselineSpend) / baselineSpend * 100)}%` : '0%',
      incrementalRevenue: option1Revenue - baselineRevenue,
      roiImprovement: baselineRoi > 0 ? Number((((option1Revenue / option1Spend) - baselineRoi) / baselineRoi * 100).toFixed(1)) : 0,
    },
    option2: {
      label: 'Maximize ROI',
      action: 'Optimize channel allocation to focus on best performers',
      impact: '+7% revenue, -8% cost',
      explanation: 'Facebook (4.2x) and Google (3.9x) show best ROAS for your scale.',
      roi: option2Spend > 0 ? Number((option2Revenue / option2Spend).toFixed(2)) : 0,
      projectedRevenue: option2Revenue,
      spend: option2Spend,
      // option2Spend guarded like its own `roi` field two lines up and like
      // option1.change: it was the one division here without one, so a zero
      // recommended spend rendered the string "NaN% ROI, 0% cost".
      change: `${Math.round(((option2Spend > 0 ? option2Revenue / option2Spend : 0) - baselineRoi) / Math.max(baselineRoi, 0.01) * 100)}% ROI, ${Math.round((option2Spend - baselineSpend) / Math.max(baselineSpend, 1) * 100)}% cost`,
    },
    keyInsight: `💡 KEY INSIGHT: Channel ROAS Optimization\nYour marketing campaigns generate ${formatBaht(baselineRevenue)}/month from ~${conversionsTotal.toLocaleString()} conversions with ${baselineRoi.toFixed(1)}x ROAS. Focus on high-performing channels to maximize results.`,
    option1BudgetBreakdown: option1Rows,
    option2BudgetBreakdown: option2Rows,
    budgetBreakdown: option1Rows,
    synergies: [
      'Facebook + TikTok combo amplifies reach by 12%',
      'TikTok awareness feeds Google Search conversions (+8% lift)',
      'LINE retargeting captures 18% of users from all channels',
    ],
    stageTransitions: [
      (() => {
        const tikTokCurrent = option1Rows.find((r) => r.name === 'TikTok')?.currentBudget ?? 0;
        const googleCurrent = option1Rows.find((r) => r.name === 'Google Search')?.currentBudget ?? 0;
        const shift = Math.round(tikTokCurrent * 0.20);
        return {
          id: 1,
          from: { channel: 'TikTok', stage: 'aware', currentBudget: Math.round(tikTokCurrent * 0.50), icon: '🎵', stageIcon: '🎯' },
          to: { channel: 'Google Search', stage: 'convert', currentBudget: Math.round(googleCurrent * 0.35), icon: '🔍', stageIcon: '💳' },
          action: `Shift ${formatBaht(shift)}: TikTok Aware → Google Convert`,
          impact: '+6% conversion rate projected',
          explanation: 'TikTok has lower ROAS (3.4x) while Google shows strong ROAS (3.9x).',
          roi: 3.9,
          projectedRevenue: Math.round(shift * 3.9),
          confidence: 75,
          lift: 6,
          currentSpend: Math.round(tikTokCurrent * 0.50) + Math.round(googleCurrent * 0.35),
          recommendedSpend: { from: Math.round(tikTokCurrent * 0.50) - shift, to: Math.round(googleCurrent * 0.35) + shift },
          type: 'cross-channel-funnel',
        };
      })(),
    ],
    funnelFlow: {
      channels: MMM_CHANNEL_STRATEGY.map((strat) => {
        const currentBudget = Math.round(channelByKey.get(strat.channelKey)?.cost ?? 0);
        return {
          name: strat.displayName,
          icon: strat.icon,
          stages: {
            aware:    { budget: Math.round(currentBudget * strat.stageMix.aware),    outgoingTransitions: [] },
            consider: { budget: Math.round(currentBudget * strat.stageMix.consider), outgoingTransitions: [] },
            convert:  { budget: Math.round(currentBudget * strat.stageMix.convert),  outgoingTransitions: [] },
          },
        };
      }),
    },
  };
};
